import React, { useState, useEffect } from "react";
import { Flame, Shield, Heart, Zap, Sparkles, Wand2, Droplet, Swords, Hourglass } from "lucide-react";

/**
 * BattleHand — iter 75.
 *
 * Renders the player's hand in BattleArena as a fanned poker-style row of
 * full-size playing cards instead of compact buttons. Each card shows:
 * - a colored frame by effect (red=damage, blue=defense, green=heal, gold=utility)
 * - mana cost inside a circular gem in the top-left
 * - a large effect-value number in the top-right
 * - an icon + card name banner
 * - a short description body
 * - a "corner" level badge if the card is upgraded (lvl > 1)
 *
 * The fan shape comes from CSS rotate + translate per position (computed
 * against the total count), so the mano si allarga naturalmente quando
 * hai più carte.
 */

const EFFECT_THEME = {
  damage: {
    frame: "from-[#6b1818] via-[#8b1a1a] to-[#3a0f0f]",
    border: "border-[#a03030]",
    accent: "text-[#ff6565]",
    accentBg: "bg-[#4a1010]",
    Icon: Swords,
    label: "DMG",
    glow: "shadow-[0_0_30px_rgba(180,40,40,0.35)]",
  },
  heal: {
    frame: "from-[#1b4d1b] via-[#267a26] to-[#0f2f0f]",
    border: "border-[#2d8a2d]",
    accent: "text-[#8aff8a]",
    accentBg: "bg-[#103a10]",
    Icon: Heart,
    label: "HEAL",
    glow: "shadow-[0_0_30px_rgba(70,180,70,0.35)]",
  },
  defense: {
    frame: "from-[#0e2a5e] via-[#1c4aa0] to-[#06193b]",
    border: "border-[#3060b0]",
    accent: "text-[#a0c4ff]",
    accentBg: "bg-[#0a1f47]",
    Icon: Shield,
    label: "DEF",
    glow: "shadow-[0_0_30px_rgba(80,140,230,0.35)]",
  },
  buff: {
    frame: "from-[#5a3a0a] via-[#8b6018] to-[#1f1408]",
    border: "border-[#cfb53b]",
    accent: "text-[#cfb53b]",
    accentBg: "bg-[#2a1d08]",
    Icon: Sparkles,
    label: "BUFF",
    glow: "shadow-[0_0_30px_rgba(207,181,59,0.35)]",
  },
  debuff: {
    frame: "from-[#3a1a4a] via-[#6a308a] to-[#1a0a22]",
    border: "border-[#8033a0]",
    accent: "text-[#d0a0ff]",
    accentBg: "bg-[#1e0c2a]",
    Icon: Droplet,
    label: "DEBUFF",
    glow: "shadow-[0_0_30px_rgba(160,60,200,0.35)]",
  },
  stun: {
    frame: "from-[#4a3a0a] via-[#a08018] to-[#221808]",
    border: "border-[#e0b040]",
    accent: "text-[#ffd873]",
    accentBg: "bg-[#2a1e06]",
    Icon: Hourglass,
    label: "STUN",
    glow: "shadow-[0_0_30px_rgba(220,170,50,0.35)]",
  },
  default: {
    frame: "from-[#2d2d35] via-[#45455a] to-[#15151a]",
    border: "border-[#60607a]",
    accent: "text-[#cfb53b]",
    accentBg: "bg-[#1f1f25]",
    Icon: Wand2,
    label: "EFFECT",
    glow: "shadow-[0_0_30px_rgba(150,150,200,0.25)]",
  },
};

function themeFor(card) {
  return EFFECT_THEME[card.effect] || EFFECT_THEME.default;
}

/**
 * Compute fanned rotation/translate for card at index `i` out of `count`.
 * When there are 1-2 cards we keep them flat; 3+ starts fanning.
 * `tight=true` (mobile) reduces the spread further so 8+ cards still fit on
 * a 360-380px viewport.
 */
function fanStyle(i, count, tight = false) {
  if (count <= 1) return {};
  if (count === 2) {
    const offset = i === 0 ? -4 : 4;
    return { transform: `rotate(${offset}deg)` };
  }
  // Center index; cards rotate around it.
  const mid = (count - 1) / 2;
  const baseSpread = tight ? 6 : 10;
  const maxSpread = Math.min(baseSpread, (tight ? 1.5 : 3) + count);
  const rot = ((i - mid) / mid) * maxSpread;
  // Small y-lift for center cards (-2 for mid, 0 for edges)
  const lift = -Math.abs(rot) * (tight ? 0.2 : 0.3);
  return { transform: `rotate(${rot.toFixed(2)}deg) translateY(${lift.toFixed(1)}px)` };
}

function CardView({ card, index, count, canPlay, disabled, onClick, lang, registerRef, tight, overlap, cardWidth, cardHeight, sizeClass }) {
  const [hover, setHover] = useState(false);
  const theme = themeFor(card);
  const Icon = theme.Icon;
  const level = card.level || 1;
  const isIt = (lang || "en").startsWith("it");
  const isAlly = card.type === "ally" || card.category === "allies";
  const typeLabel = card.type || (card.category === "allies" ? "ally" : card.category === "abilities" ? "ability" : "object");
  const hasImage = !!card.image_url;
  const isFoil = !!card.foil;
  const hasPlaque = isAlly || (typeLabel === "object" && ((card.attack || 0) > 0 || (card.defense || 0) > 0));

  return (
    <button
      type="button"
      ref={(el) => registerRef && registerRef(card.id, el)}
      onClick={onClick}
      disabled={disabled || !canPlay}
      data-testid={`play-card-${card.id}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      aria-label={`Play ${card.name}`}
      className={`relative shrink-0 transition-all duration-300 origin-bottom ${sizeClass} ${canPlay ? "cursor-pointer" : "cursor-not-allowed"}`}
      style={{
        ...fanStyle(index, count, tight),
        marginLeft: index === 0 ? 0 : `-${overlap}px`,
        zIndex: hover ? 50 : 10 + index,
        transform: hover && canPlay
          ? `${fanStyle(index, count, tight).transform || ""} translateY(${tight ? -22 : -34}px) scale(${tight ? 1.15 : 1.12})`.trim()
          : fanStyle(index, count, tight).transform,
      }}
    >
      {/* Visual MTG Card */}
      <div
        className={`mtg-card card-${typeLabel} ${card.rarity || "common"} variant-hand ${!canPlay ? "grayscale" : ""}`}
        style={{ width: cardWidth, height: cardHeight }}
      >
        <div className={`mtg-inner-frame ${isFoil ? "card-foil" : ""}`}>
          {/* Header Bar */}
          <div className="mtg-header-bar">
            <span className="mtg-card-title">{card.name}</span>
            <div className="mtg-header-right">
              {card.mana_cost > 0 && (
                <div className="mtg-mana-gem">
                  <span>{card.mana_cost}</span>
                </div>
              )}
            </div>
          </div>

          {/* Art Frame */}
          <div className="mtg-art-frame">
            {hasImage ? (
              <img
                src={card.image_url}
                alt={card.name}
                className="mtg-art-image"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <div className="mtg-no-art">
                <div className="w-7 h-7 rounded-full bg-white/[0.05] border border-white/10 flex items-center justify-center">
                  <Icon size={14} className="text-white/60" />
                </div>
              </div>
            )}
          </div>

          {/* Type Line */}
          <div className="mtg-type-line">
            <span className="mtg-type-text">
              {isAlly ? (isIt ? "ALLEATO" : "ALLY") : 
               typeLabel === "ability" ? (isIt ? "ABILITÀ" : "ABILITY") : 
               (isIt ? "OGGETTO" : "OBJECT")}
            </span>
            <div className="mtg-type-right">
              {level > 1 && (
                <span className="mtg-level-badge">Lv{level}</span>
              )}
              {card.rarity && card.rarity !== "common" && (
                <span className="mtg-rarity-indicator" style={{ color: card.rarity === "uncommon" ? "#22c55e" : card.rarity === "rare" ? "#3b82f6" : card.rarity === "epic" ? "#a855f7" : card.rarity === "legendary" ? "#f59e0b" : "#94a3b8" }}>
                  ✦
                </span>
              )}
            </div>
          </div>

          {/* Rules / Text Box */}
          <div className="mtg-rules-box">
            <div className="mtg-description">
              {card.effect && card.effect !== "none" && (card.effect_value || 0) > 0 && (
                <div className="mb-0.5 font-heading text-[7px] uppercase tracking-wider">
                  {card.effect === "damage" && <span className="text-red-600">{isIt ? "DANNO" : "DMG"}: {card.effect_value}</span>}
                  {card.effect === "heal" && <span className="text-green-600">{isIt ? "CURA" : "HEAL"}: {card.effect_value}</span>}
                  {card.effect === "buff" && <span className="text-amber-600">BUFF: +{card.effect_value}</span>}
                  {card.effect === "debuff" && <span className="text-purple-600">DEBUFF: -{card.effect_value}</span>}
                  {card.effect === "stun" && <span className="text-purple-700">STUN: {card.effect_value}t</span>}
                </div>
              )}
              <p className="line-clamp-2">{card.description}</p>
            </div>

            {/* Stat Plaque for combat stats */}
            {hasPlaque && (
              <div className="mtg-stat-plaque">
                {isAlly ? (
                  <>
                    <span>{card.attack ?? 0}</span>/<span>{card.max_hp ?? card.hp ?? 0}</span>
                  </>
                ) : (
                  <>
                    {card.attack > 0 && <span>+{card.attack}</span>}
                    {card.attack > 0 && card.defense > 0 && <span>/</span>}
                    {card.defense > 0 && <span>+{card.defense}</span>}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Unplayable overlay */}
      {!canPlay && (
        <div className="absolute inset-0 rounded-lg flex items-center justify-center bg-black/40 pointer-events-none z-30">
          <div className="bg-black/90 border border-[#a03030] rounded-full mtg-unplayable-badge flex items-center gap-1">
            <Zap className="text-[#a0c4ff] mtg-unplayable-icon" />
            <span className="font-heading text-white mtg-unplayable-text">
              {isIt ? "Mana insuff." : "Not enough mana"}
            </span>
          </div>
        </div>
      )}
    </button>
  );
}

export default function BattleHand({ hand, mana, acting, onPlayCard, lang, registerRef, deckCount = 0 }) {
  const cards = hand || [];
  const count = cards.length;

  // Iter 78 — responsive sizing to keep the fan inside the viewport on
  // mobile (375px) even with 9-10 cards. We measure the container's actual
  // width and shrink card width + increase overlap until it fits.
  const containerRef = React.useRef(null);
  const [layout, setLayout] = useState({ tight: false, cardWidth: 132, cardHeight: 188, overlap: 24 });

  useEffect(() => {
    const compute = () => {
      const containerW = containerRef.current?.clientWidth || window.innerWidth;
      const isMobileScreen = window.innerWidth < 768;
      const isTight = isMobileScreen || containerW < 450;
      // Pick a base card size; mobile is smaller, desktop/tablet bigger.
      let cardW = 132;
      if (isTight) {
        cardW = 88;
      } else if (containerW >= 1024) {
        cardW = 168; // largest size
      } else if (containerW >= 768) {
        cardW = 150; // medium-large
      }
      let cardH = Math.round(cardW * (188 / 132));
      // Footprint of the fan = first card width + (count-1)*(width-overlap).
      // Solve for overlap so footprint <= containerW - safety padding.
      // The safety accounts for the +/- rotation of edge cards which can
      // expand each card's bbox by ~10px, so we reserve 36px on tight UIs.
      const safety = isTight ? 40 : 32;
      const target = Math.max(containerW - safety, cardW + 16);
      let overlap = isTight ? 36 : 24;
      if (count > 1) {
        const step = Math.max(16, Math.floor((target - cardW) / (count - 1)));
        const clampedStep = Math.min(cardW - 14, step); // keep at least 14px visible
        overlap = Math.max(0, cardW - clampedStep);
      }
      setLayout({ tight: isTight, cardWidth: cardW, cardHeight: cardH, overlap });
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [count]);

  if (!count) {
    return (
      <div
        data-testid="player-hand-empty"
        className="text-center py-6 px-4 border border-dashed border-[#cfb53b]/20 rounded-lg"
      >
        <Flame size={28} className="mx-auto text-[#cfb53b]/30 mb-2" />
        <p className="text-xs text-[#64748b] italic">
          {(lang || "en").startsWith("it") ? "La mano è vuota" : "Your hand is empty"}
        </p>
      </div>
    );
  }

  const minH = layout.cardHeight + 28;
  const isIt = (lang || "en").startsWith("it");

  return (
    <div className="relative w-full">
      {/* Iter 79 — Hand/Deck HUD: tiny status pill above the fan */}
      <div className="absolute top-1 right-2 sm:right-4 z-30 flex items-center gap-2 text-[10px] sm:text-[11px] font-heading text-[#cfb53b] pointer-events-none">
        <span
          data-testid="hand-count"
          className="px-1.5 py-0.5 rounded bg-black/60 border border-[#cfb53b]/30"
          title={isIt ? "Carte in mano" : "Cards in hand"}
        >
          🃏 {count}/5
        </span>
        <span
          data-testid="deck-count"
          className="px-1.5 py-0.5 rounded bg-black/60 border border-[#cfb53b]/30"
          title={isIt ? "Carte nel mazzo" : "Cards in deck"}
        >
          📚 {deckCount}
        </span>
      </div>
      <div
        ref={containerRef}
        data-testid="player-hand-fan"
        className="flex items-end justify-center pt-8 sm:pt-10 pb-3 px-1 sm:px-2 relative w-full overflow-hidden"
        style={{ minHeight: `${minH}px` }}
      >
      {(() => {
        const sizeClass =
          layout.cardWidth >= 168 ? "size-xl" :
          layout.cardWidth >= 150 ? "size-lg" :
          layout.cardWidth >= 132 ? "size-md" : "size-sm";
        return cards.map((card, i) => (
          <CardView
            key={`${card.id}-${i}`}
            card={card}
            index={i}
            count={count}
            canPlay={(card.mana_cost || 0) <= (mana || 0)}
            disabled={acting}
            onClick={() => onPlayCard(card.id)}
            lang={lang}
            registerRef={registerRef}
            tight={layout.tight}
            overlap={layout.overlap}
            cardWidth={layout.cardWidth}
            cardHeight={layout.cardHeight}
            sizeClass={sizeClass}
          />
        ));
      })()}
      </div>
    </div>
  );
}
